/**
 * @fileoverview Segmentation / Tabs component
 *
 * It creates a functional tabs with panels component
 */

import BaseComponent from '../BaseComponent';
import Data from '../../utils/dom/data';
import EventHandler from '../../utils/dom/event-handler';
import Tooltip from './../Tooltip';
import LocationSearch from './../LocationSearch/LocationSearch';

const NAME = 'Tabs';

const DATA_KEY = 'lta.tabs';
const EVENT_KEY = `.${DATA_KEY}`;
const DATA_API_KEY = '.data-api';

const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`;

const SELECTOR_DATA_TOGGLE = '[data-lta-tabs]';

class Tabs extends BaseComponent {
	constructor(element) {
		super(element);

		this.initTabs();
	}

	static get NAME() {
		return NAME;
	}

	// Getters
	static get DATA_KEY() {
		return DATA_KEY;
	}

	dispose() {
		super.dispose()
		// Remove event handlers, reset constructor public settings etc.
	}

	initTabs() {
		this.tabs = this._element.querySelector(':not(.lta-tabs) .lta-tablist').querySelectorAll('.lta-tablist__tab');
		const panels = [];
		for (const panelChild of this._element.querySelectorAll(':scope .lta-tabpanel__panel')) {
			if (panelChild.closest('.lta-tabpanel') === this._element.querySelector(':scope .lta-tabpanel')) {
				panels.push(panelChild);
			}
		}

		// TODO: Could use some event delegation here.
		[...this.tabs].forEach((tab, index) => {
			tab.addEventListener('click', () => this.handleTabChange(this.tabs, panels, index));
			// Remove mouse focus styles
			tab.addEventListener('mousedown', () => {
				tab.blur();
				tab.classList.add('mouse-focus');
			});
			tab.addEventListener('mouseup', () => {
				tab.blur();
				tab.classList.remove('mouse-focus');
			});
			tab.addEventListener('mouseout', () => {
				tab.blur();
				tab.classList.remove('mouse-focus');
			});
		});

		this._element.addEventListener('keydown', (event) => this.handleKeyboardNavigation(event, this.tabs));
	}

	scrollToSelectedTabs() {
		for(const tab of this.tabs) {
			if(this.tabs[0] !== tab) {
				if(tab.classList.contains('active')) {
					tab.scrollIntoView({behavior: "auto", block: "center", inline: "center"});
				}
			}
		}
	}

	handlePanelChange(panels, index) {
		[...panels].forEach((panel_, index_) => {
			if (index == index_) {
				panel_.hidden = false;
				panel_.classList.add('active');
				const tooltip = panel_.querySelector('.js-lta-tooltip');
				if (tooltip) Tooltip.getOrCreateInstance(tooltip);

				const locationSearch = panel_.querySelector('.js-lta-location-search');
				if (locationSearch) LocationSearch.getOrCreateInstance(locationSearch);
			} else {
				panel_.hidden = true;
				panel_.classList.remove('active');
			}
		});
	}

	handleTabChange(tabs, panels, index) {
		[...tabs].forEach((tab_, index_) => {
			if (index_ == index) {
				tab_.classList.add('active');
				tab_.tabIndex = '0';
				tab_.setAttribute('aria-selected', 'true');
				this.handlePanelChange(panels, index);
			} else {
				tab_.classList.remove('active');
				tab_.tabIndex = '-1';
				tab_.setAttribute('aria-selected', 'false');
			}
		})
	}

	handleKeyboardNavigation(event) {
		const focusedElement = document.activeElement;

		if (event.key === 'ArrowRight') {
			const nextElement = focusedElement.nextElementSibling;
			if (nextElement !== null) {
				nextElement.focus();
			}
		}
		if (event.key === 'ArrowLeft') {
			const previousElement = focusedElement.previousElementSibling;
			if (previousElement !== null) {
				previousElement.focus();
			}
		}
	}
}

/**
 * Data Api implementation
 */
EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE, function (event) {
	event.preventDefault();

	const data = Data.getData(this, DATA_KEY) || new Tabs(this);
});

export default Tabs;
